import React, { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Loader2, Printer, FileDown, Eye, Pencil } from 'lucide-react'
import toast from 'react-hot-toast'
import { showSuccess, showError } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAuthStore } from '@/store/auth.store'
import { contractService } from '@/services/contractService'
import {
  getSalaryByMonth,
  getPayrollTest,
  getTestSalary,
  updateTestSalary,
  generatePayroll,
  getSalaryById,
  toApiMonthYear,
} from '@/services/payrollService'
import { payrollPaymentService } from '@/services/payrollPaymentService'
import type {
  SalaryByMonthItem,
  PayrollTestItem,
  GeneratePayrollPayload,
  SalaryById,
} from '@/api/types/payroll.types'
import type { EmployeeOption } from '@/api/types'
import { getLogoPath } from '@/components/shared/Logo'
import { PaySlipModal } from '@/modules/hr/components/PaySlipModal'
import { calendarToApi } from '@/lib/calendarDate'
import { formatCurrency } from '@/lib/format'

function getDefaultMonthYear(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function getStartEndForMonth(ym: string): { startDate: string; endDate: string } {
  const pad = (n: number) => String(n).padStart(2, '0')
  if (!ym || ym.length < 7) {
    const d = new Date()
    const y = d.getFullYear()
    const m = d.getMonth() + 1
    const lastDay = new Date(y, m, 0).getDate()
    return {
      startDate: `${y}-${pad(m)}-01`,
      endDate: `${y}-${pad(m)}-${pad(lastDay)}`,
    }
  }
  const [y, m] = ym.split('-').map(Number)
  const lastDay = new Date(y, m, 0).getDate()
  return {
    startDate: `${y}-${pad(m)}-01`,
    endDate: `${y}-${pad(m)}-${pad(lastDay)}`,
  }
}

function formatMonthYearLabel(ym?: string, locale?: string): string {
  if (!ym || ym.length < 6) return '—'
  const parts = ym.split('-')
  let monthNum: number
  let yearStr: string
  if (parts.length >= 2 && parts[0].length === 4) {
    yearStr = parts[0]
    monthNum = parseInt(parts[1], 10)
  } else if (parts.length >= 2 && parts[1].length === 4) {
    monthNum = parseInt(parts[0], 10)
    yearStr = parts[1]
  } else {
    return ym
  }
  if (monthNum >= 1 && monthNum <= 12) {
    const loc = locale?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
    const d = new Date(Number(yearStr), monthNum - 1, 1)
    return d.toLocaleDateString(loc, { month: 'long', year: 'numeric' })
  }
  return ym
}

function money(value?: number): string {
  return formatCurrency(Number(value ?? 0))
}

function n(value?: number | null): number {
  const v = Number(value ?? 0)
  return Number.isFinite(v) ? v : 0
}

function parseAmount(value: string): number {
  const v = Math.round(Number(value))
  return Number.isFinite(v) ? v : 0
}

type DisplayRow = {
  key: string
  id?: number
  empId?: number
  empCode?: string
  employeeName: string
  department?: string
  project?: string
  payrollDate?: string
  basicSalary: number
  housingAllowance: number
  transportationAllowance: number
  mealAllowance: number
  otherAllowance: number
  allowances: number
  grossPay: number
  bonus: number
  incentive: number
  overtime: number
  gosi: number
  loan: number
  absent: number
  late: number
  early: number
  otherDeductions: number
  deductions: number
  netPay: number
  source: 'salary' | 'test'
  isPaid?: boolean
  salaryRow?: SalaryByMonthItem
  testRow?: PayrollTestItem
}

type TestEditForm = {
  id: number
  empId: number
  employeeName: string
  empCode: string
  basicSalary: number
  housingAllowance: number
  transportationAllowance: number
  mealAllowance: number
  otherAllowance: number
  bonus: number
  incentive: number
  otAmount: number
  gosi: number
  absentDeduction: number
  lateDeduction: number
  earlyOutDeduction: number
  otherDeduction: number
  loanDeduction: number
}

export const Payroll: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const locale = i18n.language
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [monthYear, setMonthYear] = useState(getDefaultMonthYear)
  const defaultRange = useMemo(() => getStartEndForMonth(monthYear), [monthYear])
  const [startDate, setStartDate] = useState(defaultRange.startDate)
  const [endDate, setEndDate] = useState(defaultRange.endDate)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [salaryList, setSalaryList] = useState<SalaryByMonthItem[]>([])
  const [tested, setTested] = useState(false)
  const [testList, setTestList] = useState<PayrollTestItem[]>([])
  const [testLoading, setTestLoading] = useState(false)
  const [posting, setPosting] = useState(false)
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [paySlipOpen, setPaySlipOpen] = useState(false)
  const [paySlipData, setPaySlipData] = useState<SalaryById | null>(null)
  const [paySlipLoading, setPaySlipLoading] = useState(false)
  const [printListLoading, setPrintListLoading] = useState(false)
  const [excelListLoading, setExcelListLoading] = useState(false)
  const [tab, setTab] = useState<'employees' | 'deductions'>('employees')
  const [editOpen, setEditOpen] = useState(false)
  const [editSaving, setEditSaving] = useState(false)
  const [editForm, setEditForm] = useState<TestEditForm | null>(null)
  const [postedPaidByEmpId, setPostedPaidByEmpId] = useState<Record<number, number>>({})
  const [hasPostedPayment, setHasPostedPayment] = useState(false)

  const payrollCreated = salaryList.length > 0

  const isPaymentPosted = (value: unknown): boolean =>
    value === true || value === 1 || value === 'true' || value === 'True'

  const csvCell = (v: string | number): string => {
    const s = String(v ?? '')
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
    return s
  }

  const getEmployeeNameForRow = (row: SalaryByMonthItem): string => {
    if (row.employeeName) return row.employeeName
    const empId = row.empId ?? row.employeeId
    if (empId && employees.length > 0) {
      const emp = employees.find((e) => e.empId === empId)
      if (emp) {
        return emp.fullName ?? ([emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn].filter(Boolean).join(' ').trim() || t('salaryDetail.employeeFallback', { id: empId }))
      }
    }
    return row.empCode ? String(row.empCode) : '—'
  }

  const displayRows = useMemo<DisplayRow[]>(() => {
    if (payrollCreated) {
      return salaryList.map((row, idx) => {
        const raw = row as any
        const basic = n(raw.basicSalary)
        const housing = n(raw.housingAllowance)
        const transport = n(raw.transpotationAllowance ?? raw.transportationAllowance)
        const meal = n(raw.mealAllowance)
        const other = n(raw.otherAllowance)
        const allowances = n(raw.totalAllowance) || housing + transport + meal + other
        const bonus = n(raw.monthlyBonus ?? raw.bonus)
        const incentive = n(raw.monthlyIncentive ?? raw.incentive)
        const overtime = n(raw.otAmount)
        const gosi = n(raw.gosiDeduction ?? raw.gosi)
        const loan = n(raw.loanDeduction)
        const absent = n(raw.absentDeduction)
        const late = n(raw.lateDeduction)
        const early = n(raw.earlyOutDeduction)
        const otherDeductions = n(raw.otherDeductions ?? raw.otherDeduction)
        const deductions = n(raw.totalDeduction) || loan + gosi + absent + otherDeductions + late + early
        return {
          key: String(row.id ?? row.empId ?? idx),
          id: row.id,
          empId: row.empId ?? row.employeeId,
          empCode: row.empCode,
          employeeName: getEmployeeNameForRow(row),
          department: raw.department,
          project: raw.project,
          payrollDate: raw.payrollDate,
          basicSalary: basic,
          housingAllowance: housing,
          transportationAllowance: transport,
          mealAllowance: meal,
          otherAllowance: other,
          allowances,
          grossPay: basic + allowances,
          bonus,
          incentive,
          overtime,
          gosi,
          loan,
          absent,
          late,
          early,
          otherDeductions,
          deductions,
          netPay: n(row.totalSalary),
          source: 'salary' as const,
          salaryRow: row,
          isPaid:
            (row.empId ?? row.employeeId ?? 0) > 0 &&
            n(postedPaidByEmpId[row.empId ?? row.employeeId ?? 0]) > 0 &&
            n(postedPaidByEmpId[row.empId ?? row.employeeId ?? 0]) >= n(row.totalSalary),
        }
      })
    }
    return testList.map((row, idx) => {
      const basic = n(row.basicSalary)
      const housing = n(row.housingAllowance)
      const transport = n(row.transportationAllowance)
      const meal = n(row.mealAllowance)
      const other = n(row.otherAllowance)
      const allowances = n(row.totalAllowance) || housing + transport + meal + other
      const bonus = n(row.bonus)
      const incentive = n(row.incentive)
      const overtime = n(row.otAmount)
      const gosi = n(row.gosi)
      const loan = n(row.loanDeduction)
      const absent = n(row.absentDeduction)
      const late = n(row.lateDeduction)
      const early = n(row.earlyOutDeduction)
      const otherDeductions = n(row.otherDeduction)
      const deductions = n(row.totalDeduction) || loan + gosi + absent + otherDeductions + late + early
      return {
        key: String(row.id ?? row.empId ?? idx),
        id: row.id,
        empId: row.empId,
        empCode: row.empCode,
        employeeName: row.employeeName || row.empCode || '—',
        department: row.department,
        project: row.project,
        payrollDate: undefined,
        basicSalary: basic,
        housingAllowance: housing,
        transportationAllowance: transport,
        mealAllowance: meal,
        otherAllowance: other,
        allowances,
        grossPay: n(row.grossSalary) || basic + allowances,
        bonus,
        incentive,
        overtime,
        gosi,
        loan,
        absent,
        late,
        early,
        otherDeductions,
        deductions,
        netPay: n(row.netSalary),
        source: 'test' as const,
        testRow: row,
        isPaid: false,
      }
    })
  }, [payrollCreated, salaryList, testList, employees, locale, t, postedPaidByEmpId])

  const filteredRows = useMemo(() => {
    if (!search.trim()) return displayRows
    const q = search.toLowerCase()
    return displayRows.filter((r) =>
      r.employeeName.toLowerCase().includes(q) ||
      (r.empCode ?? '').toLowerCase().includes(q) ||
      (r.department ?? '').toLowerCase().includes(q)
    )
  }, [displayRows, search])

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, row) => ({
        payrollCost: acc.payrollCost + row.grossPay + row.bonus + row.incentive + row.overtime,
        netPay: acc.netPay + row.netPay,
        deductions: acc.deductions + row.deductions,
      }),
      { payrollCost: 0, netPay: 0, deductions: 0 }
    )
  }, [filteredRows])

  const hasData = displayRows.length > 0

  const monthPaymentStatus = useMemo(() => {
    if (!payrollCreated) return 'test' as const
    if (hasPostedPayment) return 'paid' as const
    return 'posted' as const
  }, [payrollCreated, hasPostedPayment])

  const monthStatusLabel =
    monthPaymentStatus === 'paid'
      ? t('salaryDetail.statusPaid')
      : payrollCreated
        ? t('salaryDetail.statusPosted')
        : t('salaryDetail.statusTest')

  const clearPaymentPaidStatus = () => {
    setPostedPaidByEmpId({})
    setHasPostedPayment(false)
  }

  const loadPaymentPaidStatus = async (branch: number, month: string) => {
    try {
      const payments = await payrollPaymentService.getByMonthYear(month, branch)
      const posted = (payments ?? []).filter((p) => p.id && isPaymentPosted(p.isPosted))
      setHasPostedPayment(posted.length > 0)
      if (posted.length === 0) {
        setPostedPaidByEmpId({})
        return
      }
      const lineGroups = await Promise.all(
        posted.map((p) => payrollPaymentService.getLinesByPaymentId(Number(p.id), branch))
      )
      const totals: Record<number, number> = {}
      for (const lines of lineGroups) {
        for (const line of lines ?? []) {
          const empId = Number(line.empId ?? 0)
          if (empId <= 0) continue
          totals[empId] = n(totals[empId]) + n(line.amount)
        }
      }
      setPostedPaidByEmpId(totals)
    } catch {
      clearPaymentPaidStatus()
    }
  }

  const handlePrintList = () => {
    if (!hasData) return
    setPrintListLoading(true)
    try {
      const branchName = user?.selectedBranch?.branchName ?? 'Branch'
      const dateLoc = locale?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
      const dateStr = new Date().toLocaleDateString(dateLoc, { month: 'numeric', day: '2-digit', year: '2-digit' })
      const timeStr = new Date().toLocaleTimeString(dateLoc, { hour: 'numeric', minute: '2-digit', hour12: true })
      const monthLabel = formatMonthYearLabel(monthYear, locale)
      const primaryColor = 'hsl(225, 46%, 16%)'
      const thStyle = `background:${primaryColor};color:white;padding:6px 8px;text-align:left;font-size:11px;font-weight:600;border:1px solid #e5e7eb;`
      const tdStyle = 'padding:6px 8px;font-size:11px;border:1px solid #e5e7eb;'
      const rows = displayRows.map((row) => `<tr>
          <td style="${tdStyle}">${row.empCode ?? `EMP-${row.empId ?? ''}`}</td>
          <td style="${tdStyle}">${row.employeeName}</td>
          <td style="${tdStyle}">${row.department ?? '—'}</td>
          <td style="${tdStyle}">${row.project ?? '—'}</td>
          <td style="${tdStyle}">${monthLabel}</td>
          <td style="${tdStyle}">${money(row.basicSalary)}</td>
          <td style="${tdStyle}">${money(row.housingAllowance)}</td>
          <td style="${tdStyle}">${money(row.transportationAllowance)}</td>
          <td style="${tdStyle}">${money(row.mealAllowance)}</td>
          <td style="${tdStyle}">${money(row.otherAllowance)}</td>
          <td style="${tdStyle}">${money(row.allowances)}</td>
          <td style="${tdStyle}">${money(row.bonus)}</td>
          <td style="${tdStyle}">${money(row.incentive)}</td>
          <td style="${tdStyle}">${money(row.overtime)}</td>
          <td style="${tdStyle}">${money(row.gosi)}</td>
          <td style="${tdStyle}">${money(row.loan)}</td>
          <td style="${tdStyle}">${money(row.absent)}</td>
          <td style="${tdStyle}">${money(row.late)}</td>
          <td style="${tdStyle}">${money(row.early)}</td>
          <td style="${tdStyle}">${money(row.otherDeductions)}</td>
          <td style="${tdStyle}">${money(row.deductions)}</td>
          <td style="${tdStyle}">${money(row.netPay)}</td>
        </tr>`).join('')
      const win = window.open('', '_blank')
      if (!win) {
        toast.error(t('salaryDetail.toastPopupBlocked'))
        return
      }
      win.document.write(`
        <!DOCTYPE html><html><head>
          <title>${t('salaryDetail.printReportTitle')}</title>
          <style>
            body { font-family: system-ui, sans-serif; padding: 12px; color: #1f2937; margin: 0; font-size: 12px; }
            table { border-collapse: collapse; width: 100%; }
            .header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
            .header-date { font-size: 11px; color: #6b7280; }
            .header-branch { flex: 1; text-align: center; font-weight: 600; font-size: 14px; }
            .header-logo img { height: 32px; object-fit: contain; }
            .report-title { text-align: center; font-size: 18px; font-weight: 700; margin: 12px 0 8px; }
            .header-line { height: 3px; background: ${primaryColor}; margin: 8px 0 16px; }
            @media print { body { padding: 0; } }
          </style>
        </head><body>
          <div class="header-row">
            <div class="header-date">${dateStr}, ${timeStr}</div>
            <div class="header-branch">${branchName}</div>
            <div class="header-logo"><img src="${window.location.origin}${getLogoPath()}" alt="Logo" /></div>
          </div>
          <div class="report-title">${t('salaryDetail.printReportTitle')}</div>
          <div style="text-align:center;font-size:11px;margin-bottom:12px;">${t('salaryDetail.printReportRange', { from: monthLabel, to: monthLabel })}</div>
          <div class="header-line"></div>
          <table>
            <thead>
              <tr>
                <th style="${thStyle}">${t('salaryDetail.printColEmpCode')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColEmployee')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColDepartment')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColProject')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColMonthYear')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColBasic')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColHousing')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColTransport')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColMeal')}</th>
                <th style="${thStyle}">${t('salaryDetail.testColOther')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColTotalAll')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColBonus')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColIncentive')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColOt')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColGosi')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColLoan')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColAbsent')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColLate')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColEarly')}</th>
                <th style="${thStyle}">${t('salaryDetail.colOtherDeductions')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColTotalDed')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColNetSalary')}</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body></html>
      `)
      win.document.close()
      win.focus()
      setTimeout(() => { win.print(); win.close() }, 300)
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('salaryDetail.toastPrintFailed'))
    } finally {
      setPrintListLoading(false)
    }
  }

  const handleExcelList = () => {
    if (!hasData) return
    setExcelListLoading(true)
    try {
      const monthLabel = formatMonthYearLabel(monthYear, locale)
      const headers = [
        t('salaryDetail.printColEmpCode'),
        t('salaryDetail.printColEmployee'),
        t('salaryDetail.printColDepartment'),
        t('salaryDetail.printColProject'),
        t('salaryDetail.printColMonthYear'),
        t('salaryDetail.printColBasic'),
        t('salaryDetail.printColHousing'),
        t('salaryDetail.printColTransport'),
        t('salaryDetail.printColMeal'),
        t('salaryDetail.testColOther'),
        t('salaryDetail.printColTotalAll'),
        t('salaryDetail.printColBonus'),
        t('salaryDetail.printColIncentive'),
        t('salaryDetail.printColOt'),
        t('salaryDetail.printColGosi'),
        t('salaryDetail.printColLoan'),
        t('salaryDetail.printColAbsent'),
        t('salaryDetail.printColLate'),
        t('salaryDetail.printColEarly'),
        t('salaryDetail.colOtherDeductions'),
        t('salaryDetail.printColTotalDed'),
        t('salaryDetail.printColNetSalary'),
      ]
      const rows = displayRows.map((row) => [
        csvCell(row.empCode ?? `EMP-${row.empId ?? ''}`),
        csvCell(row.employeeName),
        csvCell(row.department ?? '—'),
        csvCell(row.project ?? '—'),
        csvCell(monthLabel),
        csvCell(money(row.basicSalary)),
        csvCell(money(row.housingAllowance)),
        csvCell(money(row.transportationAllowance)),
        csvCell(money(row.mealAllowance)),
        csvCell(money(row.otherAllowance)),
        csvCell(money(row.allowances)),
        csvCell(money(row.bonus)),
        csvCell(money(row.incentive)),
        csvCell(money(row.overtime)),
        csvCell(money(row.gosi)),
        csvCell(money(row.loan)),
        csvCell(money(row.absent)),
        csvCell(money(row.late)),
        csvCell(money(row.early)),
        csvCell(money(row.otherDeductions)),
        csvCell(money(row.deductions)),
        csvCell(money(row.netPay)),
      ].join(','))
      const csv = [headers.join(','), ...rows].join('\r\n')
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${t('salaryDetail.excelFileNamePrefix')}_${monthYear || 'export'}.csv`
      a.click()
      URL.revokeObjectURL(url)
      toast.success(t('salaryDetail.toastExcelSuccess'))
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('salaryDetail.toastExcelFailed'))
    } finally {
      setExcelListLoading(false)
    }
  }

  const openPaySlip = async (row: DisplayRow) => {
    if (row.source !== 'salary' || row.id == null) {
      toast.error(t('salaryDetail.toastSalaryIdMissing'))
      return
    }
    setPaySlipLoading(true)
    setPaySlipData(null)
    setPaySlipOpen(true)
    try {
      const detail = await getSalaryById(row.id)
      setPaySlipData(detail)
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('salaryDetail.toastSalaryDetailFailed'))
      setPaySlipOpen(false)
    } finally {
      setPaySlipLoading(false)
    }
  }

  const openEditTest = (row: DisplayRow) => {
    if (!row.testRow || row.id == null) {
      toast.error(t('salaryDetail.toastTestUpdateFailed'))
      return
    }
    setEditForm({
      id: row.id,
      empId: row.empId ?? 0,
      employeeName: row.employeeName,
      empCode: row.empCode ?? '',
      basicSalary: row.basicSalary,
      housingAllowance: row.housingAllowance,
      transportationAllowance: row.transportationAllowance,
      mealAllowance: row.mealAllowance,
      otherAllowance: row.otherAllowance,
      bonus: row.bonus,
      incentive: row.incentive,
      otAmount: row.overtime,
      gosi: row.gosi,
      absentDeduction: row.absent,
      lateDeduction: row.late,
      earlyOutDeduction: row.early,
      otherDeduction: row.otherDeductions,
      loanDeduction: row.loan,
    })
    setEditOpen(true)
  }

  const editTotals = useMemo(() => {
    if (!editForm) return { allowances: 0, gross: 0, deductions: 0, net: 0 }
    const allowances =
      editForm.housingAllowance +
      editForm.transportationAllowance +
      editForm.mealAllowance +
      editForm.otherAllowance
    const gross = editForm.basicSalary + allowances
    const deductions =
      editForm.loanDeduction +
      editForm.gosi +
      editForm.absentDeduction +
      editForm.otherDeduction +
      editForm.lateDeduction +
      editForm.earlyOutDeduction
    const net = gross + editForm.bonus + editForm.incentive + editForm.otAmount - deductions
    return { allowances, gross, deductions, net }
  }, [editForm])

  const saveEditTest = async () => {
    if (!branchId || !editForm) return
    setEditSaving(true)
    try {
      const updated = await updateTestSalary({
        id: editForm.id,
        branchId: Number(branchId),
        basicSalary: editForm.basicSalary,
        housingAllowance: editForm.housingAllowance,
        transportationAllowance: editForm.transportationAllowance,
        mealAllowance: editForm.mealAllowance,
        otherAllowance: editForm.otherAllowance,
        monthlyBonus: editForm.bonus,
        monthlyIncentive: editForm.incentive,
        otAmount: editForm.otAmount,
        gosiDeduction: editForm.gosi,
        absentDeduction: editForm.absentDeduction,
        lateDeduction: editForm.lateDeduction,
        earlyOutDeduction: editForm.earlyOutDeduction,
        otherDeductions: editForm.otherDeduction,
      })
      setTestList((prev) => prev.map((row) => (row.id === updated.id ? updated : row)))
      setEditOpen(false)
      toast.success(t('salaryDetail.toastTestUpdateSuccess'))
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? e?.message ?? t('salaryDetail.toastTestUpdateFailed'))
    } finally {
      setEditSaving(false)
    }
  }

  useEffect(() => {
    if (!branchId) return
    contractService.getAllEmployees(branchId).then((data) => setEmployees(data ?? [])).catch(() => setEmployees([]))
  }, [branchId])

  const isNoDataError = (e: any): boolean => {
    const status = e?.response?.status
    const msg = String(e?.response?.data?.message ?? e?.response?.data ?? e?.message ?? '').toLowerCase()
    return status === 404 || /no data|not found|no records|no salary|no test/.test(msg)
  }

  const loadMonthData = async () => {
    if (!branchId) return
    setLoading(true)
    try {
      try {
        const res = await getSalaryByMonth(branchId, monthYear, 1, 10000)
        const list = res.data ?? []
        if (list.length > 0) {
          setSalaryList(list)
          setTestList([])
          setTested(false)
          await loadPaymentPaidStatus(branchId, monthYear)
          return
        }
        setSalaryList([])
        clearPaymentPaidStatus()
      } catch (e: any) {
        setSalaryList([])
        clearPaymentPaidStatus()
        if (!isNoDataError(e)) {
          toast.error(e?.response?.data?.message ?? t('salaryDetail.toastSalaryListFailed'))
          setTestList([])
          setTested(false)
          return
        }
      }

      try {
        const test = await getTestSalary(branchId, monthYear)
        const list = test.data ?? []
        setTestList(list)
        setTested(list.length > 0)
      } catch (e: any) {
        setTestList([])
        setTested(false)
        if (!isNoDataError(e)) {
          toast.error(e?.response?.data?.message ?? t('salaryDetail.toastTestPayrollFailed'))
        }
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setStartDate(defaultRange.startDate)
    setEndDate(defaultRange.endDate)
  }, [defaultRange.startDate, defaultRange.endDate])

  useEffect(() => {
    setSearch('')
    setTab('employees')
    if (!branchId) {
      setSalaryList([])
      setTestList([])
      setTested(false)
      clearPaymentPaidStatus()
      return
    }
    void loadMonthData()
  }, [branchId, monthYear])

  const payrollApiMessage = (e: any): string => {
    const data = e?.response?.data
    if (!data) return ''
    if (typeof data === 'string') return data
    return data.message ?? data.error ?? ''
  }

  const isAttendanceNotPosted = (e: any): boolean => {
    const msg = payrollApiMessage(e).toLowerCase()
    return e?.response?.status === 400 && msg.includes('post attendance')
  }

  const runTestPayroll = async (retest: boolean) => {
    if (!branchId) return
    setTestLoading(true)
    try {
      const res = await getPayrollTest({
        branchId,
        monthYear,
        startDate,
        endDate,
        empId: 0,
        pageNumber: 1,
        pageSize: 10000,
        save: true,
      })
      setSalaryList([])
      clearPaymentPaidStatus()
      setTestList(res.data ?? [])
      setTested(true)
      toast.success(retest ? t('salaryDetail.toastRetestPayrollSuccess') : t('salaryDetail.toastTestPayrollSaved'))
    } catch (e: any) {
      if (isAttendanceNotPosted(e)) {
        await showError(t('salaryDetail.attendanceNotPostedTitle'), t('salaryDetail.attendanceNotPostedMessage'))
      } else {
        toast.error(payrollApiMessage(e) || t('salaryDetail.toastTestPayrollFailed'))
      }
    } finally {
      setTestLoading(false)
    }
  }

  const handlePostPayroll = async () => {
    if (!branchId) return
    if (!tested || testList.length === 0) {
      toast.error(t('salaryDetail.toastTestRequiredBeforePost'))
      return
    }
    setPosting(true)
    try {
      const payload: GeneratePayrollPayload = {
        monthYear: toApiMonthYear(monthYear),
        startDate: calendarToApi(startDate),
        endDate: calendarToApi(endDate),
        branchId,
      }
      await generatePayroll(payload)
      await showSuccess(t('salaryDetail.postPayrollSuccessTitle'), t('salaryDetail.postPayrollSuccessMessage'))
      await loadMonthData()
    } catch (e: any) {
      if (isAttendanceNotPosted(e)) {
        await showError(t('salaryDetail.attendanceNotPostedTitle'), t('salaryDetail.attendanceNotPostedMessage'))
      } else {
        toast.error(payrollApiMessage(e) || t('salaryDetail.toastGenerateFailed'))
      }
    } finally {
      setPosting(false)
    }
  }

  const setEditNum = (key: keyof TestEditForm, value: string) => {
    setEditForm((prev) => (prev ? { ...prev, [key]: parseAmount(value) } : prev))
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('salaryDetail.pageTitle')} description={t('salaryDetail.pageDescription')} />
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t('salaryDetail.selectBranch')}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title={t('salaryDetail.pageTitle')} description={t('salaryDetail.pageDescription')}>
        <div className="flex flex-wrap items-center gap-2">
          {!payrollCreated && !tested && (
            <Button disabled={testLoading} onClick={() => void runTestPayroll(false)}>
              {testLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {t('salaryDetail.testPayroll')}
            </Button>
          )}
          {!payrollCreated && tested && (
            <>
              <Button variant="outline" disabled={testLoading} onClick={() => void runTestPayroll(true)}>
                {testLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {t('salaryDetail.retestPayroll')}
              </Button>
              <Button disabled={posting} onClick={() => void handlePostPayroll()}>
                {posting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {t('salaryDetail.postPayroll')}
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={handlePrintList} disabled={printListLoading || !hasData}>
            {printListLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
            {t('salaryDetail.print')}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExcelList} disabled={excelListLoading || !hasData}>
            {excelListLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
            {t('salaryDetail.excel')}
          </Button>
        </div>
      </PageHeader>

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-2 min-w-[180px]">
          <Label>{t('salaryDetail.labelMonthYear')}</Label>
          <Input type="month" value={monthYear} onChange={(e) => setMonthYear(e.target.value)} />
        </div>
        <div className="space-y-2 min-w-[180px]">
          <Label>{t('salaryDetail.labelStartDate')}</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="space-y-2 min-w-[180px]">
          <Label>{t('salaryDetail.labelEndDate')}</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        {hasData && (
          <div
            className={`pb-2 text-sm ${
              monthPaymentStatus === 'paid'
                ? 'text-green-600 dark:text-green-500'
                : monthPaymentStatus === 'posted'
                  ? 'text-blue-800 dark:text-blue-300'
                  : 'text-sky-500 dark:text-sky-400'
            }`}
          >
            {monthStatusLabel}
          </div>
        )}
      </div>

      {loading || testLoading ? (
        <Card>
          <CardContent className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : !hasData ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {t('salaryDetail.emptyMonth')}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="rounded-xl border bg-card overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 divide-y md:divide-y-0 md:divide-x">
              <div className="p-5">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t('salaryDetail.period')}
                </p>
                <p className="mt-3 text-lg font-semibold">{formatMonthYearLabel(monthYear, locale)}</p>
              </div>
              <div className="p-5 bg-muted/40">
                <div>
                  <p className="text-2xl font-semibold tracking-tight">{money(totals.payrollCost)}</p>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">
                    {t('salaryDetail.payrollCost')}
                  </p>
                </div>
                <div className="mt-4">
                  <p className="text-2xl font-semibold tracking-tight">{money(totals.netPay)}</p>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">
                    {t('salaryDetail.employeesNetPay')}
                  </p>
                </div>
              </div>
              <div className="p-5">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t('salaryDetail.colDeductions')}
                </p>
                <p className="mt-3 text-2xl font-semibold">{money(totals.deductions)}</p>
                <p className="mt-3 text-sm font-medium">
                  {t('salaryDetail.employeesCount', { count: filteredRows.length })}
                </p>
              </div>
              <div className="p-5">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">
                  {monthStatusLabel}
                </p>
                <p className="text-sm text-muted-foreground">
                  {startDate} — {endDate}
                </p>
              </div>
            </div>
          </div>

          <div className="border-b">
            <div className="flex gap-6">
              <button
                type="button"
                className={`pb-3 text-sm font-medium ${
                  tab === 'employees' ? 'border-b-2 border-primary text-foreground' : 'text-muted-foreground'
                }`}
                onClick={() => setTab('employees')}
              >
                {t('salaryDetail.tabEmployeeSummary')}
              </button>
              <button
                type="button"
                className={`pb-3 text-sm font-medium ${
                  tab === 'deductions' ? 'border-b-2 border-primary text-foreground' : 'text-muted-foreground'
                }`}
                onClick={() => setTab('deductions')}
              >
                {t('salaryDetail.tabDeductions')}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-medium">{t('salaryDetail.allEmployees')}</p>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder={t('salaryDetail.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="rounded-md border overflow-x-auto">
            {tab === 'employees' ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('salaryDetail.columnEmpCode')}</TableHead>
                    <TableHead>{t('salaryDetail.columnEmployee')}</TableHead>
                    <TableHead className="text-right">{t('salaryDetail.colGrossPay')}</TableHead>
                    <TableHead className="text-right">{t('salaryDetail.colAllowances')}</TableHead>
                    <TableHead className="text-right">{t('salaryDetail.colOthers')}</TableHead>
                    <TableHead className="text-right">{t('salaryDetail.colDeductions')}</TableHead>
                    <TableHead className="text-right">{t('salaryDetail.colBonusOt')}</TableHead>
                    <TableHead className="text-right">{t('salaryDetail.colNetPay')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.columnStatus')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.columnAction')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                        {t('salaryDetail.noData')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell className="font-medium">{row.empCode || (row.empId ? `EMP-${row.empId}` : '—')}</TableCell>
                        <TableCell className="font-medium">{row.employeeName}</TableCell>
                        <TableCell className="text-right">{money(row.grossPay)}</TableCell>
                        <TableCell className="text-right">{money(row.allowances)}</TableCell>
                        <TableCell className="text-right">{money(row.otherAllowance)}</TableCell>
                        <TableCell className="text-right">{money(row.deductions)}</TableCell>
                        <TableCell className="text-right">{money(row.bonus + row.incentive + row.overtime)}</TableCell>
                        <TableCell className="text-right">{money(row.netPay)}</TableCell>
                        <TableCell
                          className={`text-center text-sm ${
                            row.source !== 'salary'
                              ? 'text-sky-500 dark:text-sky-400'
                              : row.isPaid
                                ? 'text-green-600 dark:text-green-500'
                                : 'text-blue-800 dark:text-blue-300'
                          }`}
                        >
                          {row.source !== 'salary'
                            ? t('salaryDetail.statusTest')
                            : row.isPaid
                              ? t('salaryDetail.statusPaid')
                              : t('salaryDetail.statusPosted')}
                        </TableCell>
                        <TableCell className="text-center">
                          {row.source === 'salary' ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                              onClick={() => void openPaySlip(row)}
                              disabled={paySlipLoading}
                              title={t('salaryDetail.viewPaySlipTitle')}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditTest(row)}
                              title={t('salaryDetail.editTestAmount')}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('salaryDetail.columnEmpCode')}</TableHead>
                    <TableHead>{t('salaryDetail.columnEmployee')}</TableHead>
                    <TableHead className="text-right">{t('salaryDetail.colGosi')}</TableHead>
                    <TableHead className="text-right">{t('salaryDetail.colLoan')}</TableHead>
                    <TableHead className="text-right">{t('salaryDetail.colAbsent')}</TableHead>
                    <TableHead className="text-right">{t('salaryDetail.colLate')}</TableHead>
                    <TableHead className="text-right">{t('salaryDetail.colEarlyOut')}</TableHead>
                    <TableHead className="text-right">{t('salaryDetail.colOtherDeductions')}</TableHead>
                    <TableHead className="text-right">{t('salaryDetail.colDeductions')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.columnAction')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                        {t('salaryDetail.noData')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row) => (
                      <TableRow key={`ded-${row.key}`}>
                        <TableCell className="font-medium">{row.empCode || (row.empId ? `EMP-${row.empId}` : '—')}</TableCell>
                        <TableCell className="font-medium">{row.employeeName}</TableCell>
                        <TableCell className="text-right">{money(row.gosi)}</TableCell>
                        <TableCell className="text-right">{money(row.loan)}</TableCell>
                        <TableCell className="text-right">{money(row.absent)}</TableCell>
                        <TableCell className="text-right">{money(row.late)}</TableCell>
                        <TableCell className="text-right">{money(row.early)}</TableCell>
                        <TableCell className="text-right">{money(row.otherDeductions)}</TableCell>
                        <TableCell className="text-right">{money(row.deductions)}</TableCell>
                        <TableCell className="text-center">
                          {row.source === 'salary' ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                              onClick={() => void openPaySlip(row)}
                              disabled={paySlipLoading}
                              title={t('salaryDetail.viewPaySlipTitle')}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditTest(row)}
                              title={t('salaryDetail.editTestAmount')}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      )}

      <PaySlipModal
        open={paySlipOpen}
        onOpenChange={setPaySlipOpen}
        data={paySlipData}
        loading={paySlipLoading}
      />

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('salaryDetail.editTestTitle')}</DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4">
              <p className="text-sm font-medium">
                {editForm.empCode ? `${editForm.employeeName} (${editForm.empCode})` : editForm.employeeName}
              </p>
              <p className="text-xs text-muted-foreground">{t('salaryDetail.editLockedHint')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('salaryDetail.testColBasic')}</Label>
                  <Input type="number" value={editForm.basicSalary} disabled readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('salaryDetail.testColHousing')}</Label>
                  <Input type="number" value={editForm.housingAllowance} disabled readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('salaryDetail.testColTransport')}</Label>
                  <Input type="number" value={editForm.transportationAllowance} disabled readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('salaryDetail.testColMeal')}</Label>
                  <Input type="number" value={editForm.mealAllowance} disabled readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('salaryDetail.colOthers')}</Label>
                  <Input type="number" value={editForm.otherAllowance} onChange={(e) => setEditNum('otherAllowance', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>{t('salaryDetail.testColBonus')}</Label>
                  <Input type="number" value={editForm.bonus} disabled readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('salaryDetail.testColIncentive')}</Label>
                  <Input type="number" value={editForm.incentive} disabled readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('salaryDetail.testColOtAmount')}</Label>
                  <Input type="number" value={editForm.otAmount} disabled readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('salaryDetail.testColGosi')}</Label>
                  <Input type="number" value={editForm.gosi} disabled readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('salaryDetail.testColAbsentDeduction')}</Label>
                  <Input type="number" value={editForm.absentDeduction} disabled readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('salaryDetail.testColLateDeduction')}</Label>
                  <Input type="number" value={editForm.lateDeduction} disabled readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('salaryDetail.testColEarlyOutDeduction')}</Label>
                  <Input type="number" value={editForm.earlyOutDeduction} disabled readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('salaryDetail.colOtherDeductions')}</Label>
                  <Input type="number" value={editForm.otherDeduction} onChange={(e) => setEditNum('otherDeduction', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>{t('salaryDetail.testColLoanDeduction')}</Label>
                  <Input type="number" value={editForm.loanDeduction} disabled readOnly className="bg-muted" />
                  <p className="text-xs text-muted-foreground">{t('salaryDetail.loanReadonlyHint')}</p>
                </div>
              </div>
              <div className="rounded-md border p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">{t('salaryDetail.colAllowances')}</p>
                  <p className="font-medium">{money(editTotals.allowances)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t('salaryDetail.colGrossPay')}</p>
                  <p className="font-medium">{money(editTotals.gross)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t('salaryDetail.colDeductions')}</p>
                  <p className="font-medium">{money(editTotals.deductions)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t('salaryDetail.colNetPay')}</p>
                  <p className="font-medium">{money(editTotals.net)}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={editSaving}>
              {t('salaryDetail.cancel')}
            </Button>
            <Button onClick={() => void saveEditTest()} disabled={editSaving}>
              {editSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {t('salaryDetail.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
